import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DepositService from '@services/deposit-service';
import { ProductDraftsResponse } from '@interfaces/models/productDrafts';
import { ListingQueryKeys } from '@enums/react-query-keys';

type QueryCustomHook = {
  enabled: boolean;
};

type GetProductDraftsCustomHook = QueryCustomHook & {
  limit: number;
};

export const useGetProductDrafts = ({ enabled, limit }: GetProductDraftsCustomHook) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery<ProductDraftsResponse>({
    queryKey: [ListingQueryKeys.PRODUCT_DRAFTS_LIST],
    queryFn: ({ pageParam = 0 }) => {
      return DepositService.getProductDraftsList({
        limit,
        offset: Number(pageParam),
      });
    },
    enabled,
    retry: false,
    getNextPageParam: (lastPage, allPages) => {
      const p = allPages.length * limit;
      return p < lastPage.nbPreducts ? p : undefined;
    },
    select: (newData) => {
      const cachedData = queryClient.getQueryData<{
        pages?: ProductDraftsResponse[];
      }>([ListingQueryKeys.PRODUCT_DRAFTS_LIST]);
      const cachedDataIds = cachedData?.pages?.flatMap((page) => page.preducts).map((x) => x.id) ?? [];
      return {
        pages: [
          {
            nbPreducts: newData.pages[0].nbPreducts,
            preducts: [
              ...cachedData.pages.flatMap((page) => page.preducts),
              ...newData.pages.flatMap((page) => page.preducts).filter((x) => !cachedDataIds.includes(x.id)),
            ],
          },
        ],
        pageParams: [],
      };
    },
  });
};

type DeleteProductDraftCustomHook = {
  onSuccess: () => void;
  onError: (e: unknown) => void;
};

export const useDeleteProductDraft = ({ onSuccess, onError }: DeleteProductDraftCustomHook) => {
  const queryClient = useQueryClient();
  const { mutate: deleteProductDraft } = useMutation({
    mutationFn: (id: number) => DepositService.deleteProductDraft(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<{
        pages: ProductDraftsResponse[];
      }>([ListingQueryKeys.PRODUCT_DRAFTS_LIST], (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.flatMap((page) => ({
            ...page,
            preducts: page.preducts.filter((row) => row.id !== id),
          })),
        };
      });
      onSuccess();
    },
    onError: (e) => {
      onError(e);
    },
  });

  return { deleteProductDraft };
};
